import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { aiAssistantAPI } from '../api/client'
import BrandWordmark from './BrandWordmark'
import { OPEN_AI_ASSISTANT_EVENT } from '../utils/aiAssistantEvents'
import { sanitizeAiHistory, sanitizeAiReply } from '../utils/sanitizeAiReply'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

const PRESET_QUESTIONS_LT: string[] = [
  'Kaip teisingai fotografuoti telefoną?',
  'Ką reiškia būklė „Good"?',
  'Kodėl svarbu IMEI patikra?',
  'Kaip vyksta apmokėjimas ir siuntimas?',
]

const STORAGE_HISTORY = 'redevicer_ai_bubble_history_v2'
const LEGACY_STORAGE_HISTORY = 'redevicer_ai_bubble_history_v1'

const BUBBLE_SIZE = 60
const PANEL_W = 380
const PANEL_H = 520

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v))
}

function isMobileViewport() {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(max-width: 960px)').matches
}

/** Keep bubble above mobile tab bar + safe area. */
function bubbleBottomInset() {
  if (!isMobileViewport()) return 80
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--vc-tabbar-clearance').trim()
  const clearance = parseFloat(raw)
  return (Number.isFinite(clearance) ? clearance : 118) + 8
}

function defaultBubblePosition() {
  const inset = bubbleBottomInset()
  return {
    x: window.innerWidth - BUBBLE_SIZE - 16,
    y: window.innerHeight - BUBBLE_SIZE - inset,
  }
}

type Props = {
  token: string | null
}

/** Draggable floating AI assistant bubble */
export default function AIAssistantBubble({ token }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [available, setAvailable] = useState<boolean | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  // Dragging state
  const [pos, setPos] = useState({ x: -1, y: -1 }) // -1 = use CSS default
  const dragging = useRef(false)
  const dragStart = useRef({ mx: 0, my: 0, bx: 0, by: 0 })
  const bubbleRef = useRef<HTMLButtonElement | null>(null)

  const [isDesktop, setIsDesktop] = useState<boolean>(() => !isMobileViewport())

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 961px)')
    const sync = () => setIsDesktop(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  // Init position bottom-right (above tab bar on mobile)
  useEffect(() => {
    if (pos.x !== -1) return
    setPos(defaultBubblePosition())
  }, [pos.x])

  useEffect(() => {
    if (typeof window === 'undefined') return
    function onResize() {
      setPos((prev) => {
        if (prev.x < 0) return defaultBubblePosition()
        const inset = bubbleBottomInset()
        const maxX = window.innerWidth - BUBBLE_SIZE - 8
        const maxY = window.innerHeight - BUBBLE_SIZE - inset
        return {
          x: clamp(prev.x, 8, maxX),
          y: clamp(prev.y, 8, maxY),
        }
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, loading])

  const refreshStatus = useCallback(() => {
    aiAssistantAPI
      .status()
      .then((res) => setAvailable(!!res.data?.configured))
      .catch(() => {
        // Transient errors — don't show "unavailable" if chat may still work
        setAvailable((prev) => (prev === true ? true : null))
      })
  }, [])

  useEffect(() => {
    refreshStatus()
  }, [refreshStatus])

  useEffect(() => {
    if (open) refreshStatus()
  }, [open, refreshStatus])

  useEffect(() => {
    function onOpenRequest() { setOpen(true) }
    window.addEventListener(OPEN_AI_ASSISTANT_EVENT, onOpenRequest)
    return () => window.removeEventListener(OPEN_AI_ASSISTANT_EVENT, onOpenRequest)
  }, [])

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  useEffect(() => {
    const fallback = t('ai.unconfigured')
    try {
      const raw = localStorage.getItem(STORAGE_HISTORY) ?? localStorage.getItem(LEGACY_STORAGE_HISTORY)
      if (!raw) return
      const parsed = JSON.parse(raw) as ChatMessage[]
      if (!Array.isArray(parsed)) return
      setMessages(sanitizeAiHistory(parsed, fallback))
      if (localStorage.getItem(LEGACY_STORAGE_HISTORY)) {
        localStorage.removeItem(LEGACY_STORAGE_HISTORY)
      }
    } catch { /* noop */ }
  }, [t])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_HISTORY, JSON.stringify(messages.slice(-30)))
    } catch { /* noop */ }
  }, [messages])

  // ── Drag handlers ──────────────────────────────────────────────────────
  function startDrag(clientX: number, clientY: number) {
    dragging.current = true
    dragStart.current = { mx: clientX, my: clientY, bx: pos.x, by: pos.y }
  }

  useEffect(() => {
    function onMove(e: MouseEvent | TouchEvent) {
      if (!dragging.current) return
      const cx = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX
      const cy = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY
      const dx = cx - dragStart.current.mx
      const dy = cy - dragStart.current.my
      const inset = bubbleBottomInset()
      const maxX = window.innerWidth - BUBBLE_SIZE - 8
      const maxY = window.innerHeight - BUBBLE_SIZE - inset
      setPos({
        x: clamp(dragStart.current.bx + dx, 8, maxX),
        y: clamp(dragStart.current.by + dy, 8, maxY),
      })
    }
    function onUp() { dragging.current = false }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('mouseup', onUp)
    window.addEventListener('touchend', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('touchend', onUp)
    }
  }, [pos])

  const sendMessage = useCallback(
    (text: string) => {
      const content = text.trim()
      if (!content || loading) return
      const newHistory: ChatMessage[] = [...messages, { role: 'user', content }]
      setMessages(newHistory)
      setInput('')
      setLoading(true)
      aiAssistantAPI
        .chat(newHistory.slice(-20), token ?? undefined)
        .then((res) => {
          setAvailable(true)
          const raw = (res.data?.reply as string) || t('ai.fallback')
          const reply = sanitizeAiReply(raw, t('ai.unconfigured'))
          setMessages((prev) => [...prev, { role: 'assistant', content: reply }])
        })
        .catch(() => {
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: t('ai.networkError') },
          ])
        })
        .finally(() => setLoading(false))
    },
    [messages, loading, t, token],
  )

  function clearHistory() {
    setMessages([])
    try {
      localStorage.removeItem(STORAGE_HISTORY)
      localStorage.removeItem(LEGACY_STORAGE_HISTORY)
    } catch { /* noop */ }
  }

  if (pos.x === -1) return null

  const panelLeft = isDesktop
    ? clamp(pos.x - PANEL_W + BUBBLE_SIZE, 8, window.innerWidth - PANEL_W - 8)
    : 0
  const panelTop = isDesktop
    ? (pos.y - PANEL_H - 12 < 8 ? pos.y + BUBBLE_SIZE + 12 : pos.y - PANEL_H - 12)
    : 0

  return (
    <>
      <button
        ref={bubbleRef}
        type="button"
        className={`ai-bubble${open ? ' ai-bubble--open' : ''}${isDesktop ? '' : ' ai-bubble--mobile'}`}
        style={{ left: pos.x, top: pos.y }}
        aria-label={t('ai.openLabel')}
        onMouseDown={(e) => {
          e.preventDefault()
          startDrag(e.clientX, e.clientY)
        }}
        onTouchStart={(e) => {
          startDrag(e.touches[0].clientX, e.touches[0].clientY)
        }}
        onClick={() => {
          if (!dragging.current) setOpen((v) => !v)
        }}
      >
        <img src="/logo-mark.png?v=4" alt="" className="ai-bubble__logo" width={36} height={36} />
      </button>

      {open && (
        <div
          className={isDesktop ? 'ai-panel' : 'ai-sheet'}
          style={
            isDesktop
              ? {
                  left: panelLeft,
                  top: panelTop,
                  width: PANEL_W,
                  height: Math.min(PANEL_H, window.innerHeight - 32),
                }
              : undefined
          }
          role="dialog"
          aria-modal="true"
          aria-label={t('ai.title')}
        >
          <header className="ai-panel__head">
            <div className="ai-panel__brand">
              <div className="ai-panel__brand-lockup">
                <BrandWordmark className="brand-wordmark--ai-panel" title="" />
              </div>
              <span className="ai-panel__brand-suffix">{t('ai.suffix')}</span>
            </div>
            <div className="ai-panel__head-actions">
              <button type="button" className="ai-modal__clear" onClick={clearHistory}>
                {t('ai.clear')}
              </button>
              <button
                type="button"
                className="ai-modal__close"
                onClick={() => setOpen(false)}
                aria-label={t('ai.close')}
              >×</button>
            </div>
          </header>

          {available === false && (
            <p className="ai-modal__warning">{t('ai.unconfigured')}</p>
          )}

          <div className="ai-modal__body" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="ai-modal__greet">
                <p>{t('ai.greeting')}</p>
                <div className="ai-modal__presets">
                  {PRESET_QUESTIONS_LT.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="ai-modal__preset"
                      disabled={loading}
                      onClick={() => sendMessage(q)}
                    >{q}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`ai-msg ai-msg--${m.role}`}>
                <div className="ai-msg__bubble">{m.content}</div>
              </div>
            ))}
            {loading && (
              <div className="ai-msg ai-msg--assistant">
                <div className="ai-msg__bubble ai-msg__bubble--typing" role="status" aria-live="polite">
                  <span className="ai-typing" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                  <span className="ai-typing-label">{t('ai.thinking')}</span>
                </div>
              </div>
            )}
          </div>

          <form
            className="ai-modal__form"
            onSubmit={(e) => { e.preventDefault(); sendMessage(input) }}
          >
            <input
              type="text"
              className="ai-modal__input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('ai.inputPlaceholder')}
              disabled={loading}
              maxLength={1000}
              autoFocus
            />
            <button type="submit" className="ai-modal__send" disabled={loading || !input.trim()}>
              {t('ai.send')}
            </button>
          </form>
        </div>
      )}
    </>
  )
}
